/**
 * Where the bot's orders actually sit in the book, and what has to trade
 * before they get hit.
 *
 * The ladder above tells you a resting order is 0.72% away. That is not the
 * question a grid operator has. The question is how much stock is queued in
 * front of it, because an order 0.2% away behind 40,000 shares is further from
 * filling than one 0.8% away with the level to itself.
 *
 * Queue arithmetic, per side:
 *   BUY at P  — sellers hit the highest bids first, so every bid ABOVE P
 *               clears before us, plus whatever sits at P already (we joined
 *               the back of that queue when we posted).
 *   SELL at P — buyers lift the lowest asks first, so every ask BELOW P
 *               clears before us, plus the rest of our own level.
 *
 * The same-level figure is an upper bound: anyone who joined behind us is
 * counted as ahead, because aggregated depth (MBP) cannot say who queued when.
 * It errs towards "further away", which is the safe direction.
 *
 * Every number here comes from the market feed — the AHL depth stream and our
 * own stored orders. NOTHING is asked of the broker: the bots hold the only
 * StockIntel session and a second connection would knock them offline.
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { BotDetail, BotOrderView } from '../../lib/bots';
import type { DepthLevel, LiveState } from '../../lib/liveFeed';
import { Card } from '../../components/UI';
import { Caption, CardHead, Empty, fmt, fmtInt, mono, pktTime, pnlColor, signed } from './ui';

interface Props {
  bot: BotDetail;
  live: LiveState;
}

interface Queue {
  order: BotOrderView;
  /** Shares that must trade before ours can, or null when our price is off the visible book. */
  ahead: number | null;
  /** Levels between the touch and us. */
  levelsAhead: number;
  /** Is our price inside the range the feed is showing? */
  visible: boolean;
  /** Nothing in front: we are the best bid or the best offer. */
  atTouch: boolean;
}

/** Shares queued in front of `order` at `order.price`, given this side of the book. */
function queueFor(order: BotOrderView | undefined, levels: DepthLevel[], side: 'BUY' | 'SELL'): Queue | null {
  if (!order) return null;
  const better = side === 'BUY' ? levels.filter((l) => l.price > order.price) : levels.filter((l) => l.price < order.price);
  const same = levels.find((l) => Math.abs(l.price - order.price) < 0.005);
  const remaining = order.quantity - order.executed;
  // Our own order is part of the aggregate at our level; subtract it back out.
  const sameAhead = same ? Math.max(0, same.qty - remaining) : 0;
  // Beyond the levels the feed publishes we cannot count what is in front.
  const worst = side === 'BUY' ? Math.min(...levels.map((l) => l.price)) : Math.max(...levels.map((l) => l.price));
  const visible = levels.length > 0 && (side === 'BUY' ? order.price >= worst : order.price <= worst);
  return {
    order,
    ahead: visible ? better.reduce((s, l) => s + l.qty, 0) + sameAhead : null,
    levelsAhead: better.length,
    visible,
    atTouch: visible && better.length === 0 && sameAhead === 0,
  };
}

export const BookQueue = ({ bot, live }: Props) => {
  const mbp = live.mbp;
  const buy = bot.resting.find((o) => o.side === 'BUY' && o.role !== 'ACQUIRE');
  const sell = bot.resting.find((o) => o.side === 'SELL');
  const dayVolume = live.l1?.volume;

  const bidQ = useMemo(() => queueFor(buy, mbp?.bids ?? [], 'BUY'), [buy, mbp]);
  const askQ = useMemo(() => queueFor(sell, mbp?.asks ?? [], 'SELL'), [sell, mbp]);

  if (!buy && !sell) return <Empty>Nothing resting to queue.</Empty>;
  if (!mbp) {
    return (
      <Empty>
        {live.socket === 'open' ? `Waiting for ${bot.params.symbol} depth…` : 'Connecting to the market feed…'}
      </Empty>
    );
  }

  const last = live.l1?.last ?? bot.position.mark.last ?? null;
  // Asks high→low above the mark, bids high→low below it: one continuous price axis.
  const asks = [...mbp.asks].sort((a, b) => b.price - a.price);
  const bids = [...mbp.bids].sort((a, b) => b.price - a.price);
  const widest = Math.max(1, ...asks.map((l) => l.qty), ...bids.map((l) => l.qty));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <QueueHead q={bidQ} side="BUY" dayVolume={dayVolume} />
        <QueueHead q={askQ} side="SELL" dayVolume={dayVolume} />
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 10.5, color: 'var(--tk-ink-3)', padding: '0 2px 4px', ...mono }}>
          {mbp.bids.length + mbp.asks.length} levels · {pktTime(mbp.receivedAt)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '78px 1fr 64px', gap: 8, fontSize: 10, letterSpacing: '0.07em', color: 'var(--tk-ink-3)', fontWeight: 600, padding: '0 2px 6px' }}>
          <span>PRICE</span>
          <span>DEPTH</span>
          <span style={{ textAlign: 'right' }}>ORDERS</span>
        </div>
        {asks.map((l) => (
          <Row key={`a${l.price}`} level={l} side="SELL" widest={widest} order={sell} />
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 2px', borderTop: '1px solid var(--tk-line)', borderBottom: '1px solid var(--tk-line)', margin: '3px 0' }}>
          <span style={{ fontSize: 10, letterSpacing: '0.07em', color: 'var(--tk-ink-3)', fontWeight: 700 }}>LAST</span>
          <span style={{ fontSize: 14, fontWeight: 700, ...mono }}>{last ? fmt(last) : '—'}</span>
          <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--tk-ink-3)', ...mono }}>
            {dayVolume ? `${fmtInt(dayVolume)} sh today` : ''}
          </span>
        </div>
        {bids.map((l) => (
          <Row key={`b${l.price}`} level={l} side="BUY" widest={widest} order={buy} />
        ))}
      </div>

      <Caption>
        Shares ahead counts every order at a better price plus the rest of our own level. Our queue position within a level is
        not published, so anyone who joined behind us is counted as ahead — the figure is an upper bound.
      </Caption>
    </div>
  );
};

/** The headline number for one side: what has to clear before we trade. */
const QueueHead = ({ q, side, dayVolume }: { q: Queue | null; side: 'BUY' | 'SELL'; dayVolume?: number }) => {
  const color = side === 'BUY' ? 'var(--tk-up)' : 'var(--tk-down)';
  const soft = side === 'BUY' ? 'var(--tk-up-soft)' : 'var(--tk-down-soft)';
  if (!q) {
    return (
      <div style={{ padding: 14, borderRadius: 'var(--tk-radius-sm)', background: 'var(--tk-surface-2)' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--tk-ink-3)' }}>{side}</span>
        <div style={{ fontSize: 13, color: 'var(--tk-ink-3)', marginTop: 6 }}>{side === 'BUY' ? 'At the cap — no buy leg.' : 'Nothing to sell.'}</div>
      </div>
    );
  }
  const pctOfDay = q.ahead !== null && dayVolume ? (q.ahead / dayVolume) * 100 : null;
  return (
    <div style={{ padding: 14, borderRadius: 'var(--tk-radius-sm)', background: soft }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color }}>{side}</span>
        <span style={{ fontSize: 11.5, color: 'var(--tk-ink-2)', ...mono }}>@ {fmt(q.order.price)}</span>
      </div>
      {q.atTouch ? (
        <div style={{ fontSize: 19, fontWeight: 700, color, marginTop: 8, letterSpacing: '-0.02em' }}>First in the queue</div>
      ) : q.ahead === null ? (
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--tk-ink-2)', marginTop: 8 }}>Beyond the published book</div>
      ) : (
        <>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6, letterSpacing: '-0.03em', ...mono }}>{fmtInt(q.ahead)}</div>
          <div style={{ fontSize: 11.5, color: 'var(--tk-ink-2)' }}>
            shares ahead{q.levelsAhead ? ` · ${q.levelsAhead} level${q.levelsAhead === 1 ? '' : 's'}` : ''}
          </div>
        </>
      )}
      {pctOfDay !== null && !q.atTouch && (
        <div style={{ fontSize: 10.5, color: 'var(--tk-ink-3)', marginTop: 6, ...mono }}>{pctOfDay.toFixed(1)}% of today’s volume</div>
      )}
    </div>
  );
};

/** One price level, with our own order called out where it sits. */
const Row = ({ level, side, widest, order }: { level: DepthLevel; side: 'BUY' | 'SELL'; widest: number; order?: BotOrderView }) => {
  const color = side === 'BUY' ? 'var(--tk-up)' : 'var(--tk-down)';
  const ours = order && Math.abs(level.price - order.price) < 0.005;
  const remaining = order ? order.quantity - order.executed : 0;
  return (
    <div
      title={ours ? `Your ${side} for ${fmtInt(remaining)} sh sits in this level's ${fmtInt(level.qty)} sh` : undefined}
      style={{ display: 'grid', gridTemplateColumns: '78px 1fr 64px', gap: 8, alignItems: 'center', padding: '3px 2px', borderRadius: 4, background: ours ? 'var(--tk-accent-soft)' : undefined }}
    >
      <span style={{ fontSize: 12, fontWeight: ours ? 700 : 500, color: ours ? 'var(--tk-ink)' : color, ...mono }}>{fmt(level.price)}</span>
      <div style={{ position: 'relative', height: 17, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, width: `${(level.qty / widest) * 100}%`, background: color, opacity: ours ? 0.3 : 0.16, borderRadius: 3 }} />
        <span style={{ position: 'relative', fontSize: 11.5, paddingLeft: 6, ...mono }}>{fmtInt(level.qty)}</span>
        {ours && (
          <span style={{ position: 'relative', marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--tk-accent)', whiteSpace: 'nowrap' }}>
            YOURS {fmtInt(remaining)}
          </span>
        )}
      </div>
      <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', textAlign: 'right', ...mono }}>{fmtInt(level.orders)}</span>
    </div>
  );
};

/**
 * The prints themselves, newest first.
 *
 * The book says what is *offered*; the tape says what actually traded. A grid
 * operator reads them against each other — a sell rung with thin depth above it
 * means little if nothing is printing up there.
 *
 * Direction is inferred by comparing each print to the one before it, since the
 * feed does not say which side initiated. The first row has nothing to compare
 * against and is left neutral.
 */
export const Tape = ({ live, sym }: { live: LiveState; sym: string }) => {
  const trades = live.trades.filter((t) => t.sym.toUpperCase() === sym.toUpperCase());
  if (trades.length === 0) {
    return <Empty>{live.socket === 'open' ? `No prints on ${sym} yet.` : 'Connecting to the market feed…'}</Empty>;
  }
  const widest = Math.max(1, ...trades.map((t) => t.volume));
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 10.5, color: 'var(--tk-ink-3)', padding: '0 2px 4px', ...mono }}>
        {trades.length} prints · {trades[0].time.slice(0, 8)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '62px 1fr 76px', gap: 8, fontSize: 10, letterSpacing: '0.07em', color: 'var(--tk-ink-3)', fontWeight: 600, padding: '0 2px 6px' }}>
        <span>TIME</span>
        <span>PRICE</span>
        <span style={{ textAlign: 'right' }}>SHARES</span>
      </div>
      {trades.slice(0, 60).map((t, i) => {
        const prev = trades[i + 1];
        const dir = !prev || t.price === prev.price ? 0 : t.price > prev.price ? 1 : -1;
        const color = dir === 0 ? 'var(--tk-ink-2)' : dir > 0 ? 'var(--tk-up)' : 'var(--tk-down)';
        return (
          <div
            key={`${t.tsMs}-${i}`}
            title={t.coalesced > 1 ? `${t.coalesced} prints coalesced into one push` : undefined}
            style={{ display: 'grid', gridTemplateColumns: '62px 1fr 76px', gap: 8, alignItems: 'center', padding: '3px 2px' }}
          >
            <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', ...mono }}>{t.time.slice(0, 8)}</span>
            <div style={{ position: 'relative', height: 17, display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'absolute', inset: 0, width: `${(t.volume / widest) * 100}%`, background: color, opacity: 0.14, borderRadius: 3 }} />
              <span style={{ position: 'relative', fontSize: 12, fontWeight: dir === 0 ? 500 : 600, color, paddingLeft: 6, ...mono }}>{fmt(t.price)}</span>
            </div>
            <span style={{ fontSize: 11.5, textAlign: 'right', ...mono }}>{fmtInt(t.volume)}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * What the market itself is doing, straight off the L1 stream — the context a
 * grid's own numbers cannot give you.
 *
 * Every figure here is pushed by the exchange feed rather than polled, so it
 * moves as the market moves. Nothing in it is derived from our orders or our
 * position: this is the market the bot is quoting into, and it is the first
 * thing to look at when the bot's behaviour stops making sense.
 */
export const MarketInfo = ({ live, sym }: { live: LiveState; sym: string }) => {
  const l1 = live.l1;
  // Against the previous close, which is what the exchange itself quotes a
  // change against — not against the open, and not against our reference.
  const base = l1?.close || 0;
  const change = l1 && base ? l1.last - base : 0;
  const changePct = base ? (change / base) * 100 : 0;
  const tone = pnlColor(change);
  // Where the last print sits between the day's extremes: a bot quoting near
  // the low of the day is in a very different market from one near the high.
  const span = l1 && l1.high > l1.low ? (l1.last - l1.low) / (l1.high - l1.low) : null;
  const spread = l1 && l1.ask > 0 && l1.bid > 0 ? l1.ask - l1.bid : null;

  const Cell = ({ label, value, color, title }: { label: string; value: ReactNode; color?: string; title?: string }) => (
    <div title={title} style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={{ fontSize: 9.5, color: 'var(--tk-ink-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: color ?? 'var(--tk-ink)', whiteSpace: 'nowrap', ...mono }}>{value}</span>
    </div>
  );

  return (
    <Card padding={20}>
      <CardHead
        title="Market"
        right={l1 ? `${sym} · ${l1.status || 'REG'} · ${l1.time || pktTime(l1.receivedAt)}` : sym}
      />
      {!l1 ? (
        <Empty>Waiting for the first quote…</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: tone, ...mono }}>{fmt(l1.last)}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: tone, ...mono }}>
              {signed(change)} ({signed(changePct)}%)
            </span>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', ...mono }} title="Best bid and ask, with the size resting at each">
              {fmtInt(l1.bidQty)} @ {fmt(l1.bid)} / {fmt(l1.ask)} @ {fmtInt(l1.askQty)}
            </span>
          </div>

          {/* The day's range, with where the last print sits inside it. */}
          {span !== null && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ position: 'relative', height: 4, borderRadius: 999, background: 'linear-gradient(90deg, var(--tk-down) 0%, var(--tk-ink-3) 50%, var(--tk-up) 100%)', opacity: 0.45 }}>
                <span
                  className="tkb-marker"
                  style={{ position: 'absolute', top: -3, left: `${Math.max(0, Math.min(1, span)) * 100}%`, width: 10, height: 10, marginLeft: -5, borderRadius: 999, background: 'var(--tk-ink)', border: '2px solid var(--tk-surface)' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--tk-ink-3)', ...mono }}>
                <span>low {fmt(l1.low)}</span>
                <span>high {fmt(l1.high)}</span>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(84px, 1fr))', gap: 14 }}>
            <Cell label="Open" value={fmt(l1.open)} />
            <Cell label="Prev close" value={fmt(l1.close)} title="Yesterday's close — what the change above is measured from" />
            <Cell label="VWAP" value={fmt(l1.average)} color={l1.last >= l1.average ? 'var(--tk-up)' : 'var(--tk-down)'} title="The day's volume-weighted average price. Trading above it means the later part of the day has been dearer than the earlier." />
            <Cell label="Volume" value={fmtInt(l1.volume)} title="Shares traded today" />
            <Cell label="Trades" value={fmtInt(l1.trades)} title="Number of prints today — volume divided by this is the average trade size" />
            <Cell label="Avg trade" value={l1.trades > 0 ? fmtInt(l1.volume / l1.trades) : '—'} title="Shares per print. Our lot size against this says how big our orders are for this book." />
            <Cell label="Spread" value={spread === null ? '—' : `${fmt(spread)}`} color={spread !== null && l1.bid > 0 ? pnlColor(-(spread / l1.bid) * 100) : undefined} title="Ask less bid — what crossing the book costs right now" />
            <Cell label="Turnover" value={fmtInt(l1.volume * l1.average)} title="Shares traded times the day's average price" />
          </div>
        </div>
      )}
    </Card>
  );
};
